import { get } from '@rover/react-lib/src/apis/hrefLangs.api';
import fetchHrefLangsResponseFactory from '@rover/react-lib/src/factories/fetchHrefLangsResponseFactory';
import type {
  ActionCreator,
  Duck,
  DuckOptions,
  EffectCreator,
  SelectorCreator,
} from '@rover/react-lib/src/lib/redux-duck';
import { createDuck } from '@rover/react-lib/src/lib/redux-duck';
import type { FetchHrefLangsResponse } from '@rover/types';
import { ApiFrontendHrefLangsRetrieve200ResultsItem } from '@rover/rsdk/src/apiClient/latest';
import { captureError } from '@rover/rsdk/src/modules/ErrorReporting';

import generateActions from './generateActions';

export type State = {
  errorMessage: string;
  loading: boolean;
  fetchHrefLangsResponse: FetchHrefLangsResponse;
};

type Actions = {
  fetchHrefLangsPending: ActionCreator<void>;
  fetchHrefLangsSuccess: ActionCreator<FetchHrefLangsResponse>;
  fetchHrefLangsFailure: ActionCreator<string>;
};

type Effects = {
  fetchHrefLangs: EffectCreator<string>;
};

type Selectors = {
  getHrefLangs: SelectorCreator<ApiFrontendHrefLangsRetrieve200ResultsItem[]>;
};

type Options = DuckOptions<State, Actions, Effects, Selectors>;

export const options: Options = {
  name: 'hrefLangs',
  initialState: {
    errorMessage: '',
    loading: true,
    fetchHrefLangsResponse: fetchHrefLangsResponseFactory(),
  },
  actions: { ...generateActions('fetchHrefLangs') },
  effects: {
    fetchHrefLangs:
      (path: string) =>
      async (dispatch, getState, duckActions): Promise<void> => {
        dispatch(duckActions.fetchHrefLangsPending());

        try {
          const fetchHrefLangsResponse: FetchHrefLangsResponse = await get({
            path,
          });
          dispatch(duckActions.fetchHrefLangsSuccess(fetchHrefLangsResponse));
        } catch (error) {
          dispatch(duckActions.fetchHrefLangsFailure((error as Error).message));
          captureError(error as Error);
        }
      },
  },
  selectors: {
    getHrefLangs: (getState, createSelector) =>
      createSelector([getState], (s) => s.fetchHrefLangsResponse.results),
  },
};

export const { actions, selectors, effects, reducer } = createDuck(options) as Duck<
  State,
  Actions,
  Effects,
  Selectors
>;
